import { defineStore } from 'pinia';
import { notificationRepository } from '@/service/repository/notificationRepository';
import { copilotRepository } from '@/service/repository/copilotRepository';
import store from '@/store';
import { filterPendingByCopilotSelectedRole } from '@/utils';
import { notificationMatchesZoneOrRangeFilter } from '@/utils/unassignedReservationZone';

/** Staff upselling KPI report — safe to cache; not the pending notification queue. */
const UPSELLING_REPORT_TTL_MS = 10 * 60 * 1000;

/** Staff incentive goals progress — safe to cache for upselling product suggestions. */
const INCENTIVE_GOALS_TTL_MS = 10 * 60 * 1000;

function isUpsellingReportEnabled() {
	const v = store.getters['venue/copilotConfig']?.upsellingDrinksReminder;
	return v != null && v !== 0;
}

export const useNotificationStore = defineStore('notification', {
	state: () => ({
		notificationsType: {
			SECOND_DRINK: 'upselling_drinks_reminder',
			KIOSK_WALK_IN: 'kiosk_walk_in',
			KIOSK_QR_SCAN: 'kiosk_qr_scan',
			CONSUMER_READY_TO_ORDER: 'customer_ready_to_order',
			ACCOUNT_SEATED: 'account_seated',
			TABLE_TAKE_ORDER_REMINDER: 'table_take_order_reminder',
			TABLE_CLEAN_REMINDER: 'table_clean_reminder',
		},

		notificationUpsellingActions: {
			NOTHING: 'nothing',
			CART: 'cart',
		},
		// Recent muestra los últimos 10min
		// Main notifications array containing the unified response
		notifications: { history: [], pending: [] }, // Contains { history: [...], pending: [...] }

		isLoadingNotifications: false,
		isLoadingTicketNotifications: false,
		lastUpdateTime: null,
		upsellingNotificationsData: [],
		lastUpsellingFetchAt: null,
		isLoadingUpsellingReport: false,
		incentiveGoalsProgress: [],
		incentiveGoalsStaffId: null,
		lastIncentiveGoalsFetchAt: null,
		isLoadingIncentiveGoals: false,
	}),

	getters: {
		// Main getter
		getNotifications: (state) => state.notifications,

		// Get history and pending from the unified response
		getHistoryNotifications: (state) => filterPendingByCopilotSelectedRole(state.notifications?.history || []),
		getPendingNotifications: (state) => filterPendingByCopilotSelectedRole(state.notifications?.pending || []),

		// Get pending notifications filtered by type
		getPendingTicketNotifications: (state) => {
			const list = state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || [];
			return filterPendingByCopilotSelectedRole(list);
		},
		getPendingUpsellingNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(state, (n) => n.type === 'upselling'),
			),

		getAllPendingAccountKioskNotification: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(n) => (n.type === 'kiosk_walk_in' || n.type === 'kiosk_qr_scan') && n.status === 0,
				),
			),

		getAllCustomerReadyToOrderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(n) => n.status === 0 && n.type === state.notificationsType.CONSUMER_READY_TO_ORDER,
				),
			),

		getAllAccountSeatedNotifications: (state) => {
			const seated = filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.ACCOUNT_SEATED);
			const takeOrder = filterPendingByTypeAndCopilotZoneRange(
				state,
				state.notificationsType.TABLE_TAKE_ORDER_REMINDER,
			);
			const takeOrderWids = new Set(takeOrder.map((n) => notificationReservationWid(n)).filter((w) => w != null));
			const merged = seated.filter((n) => {
				const wid = notificationReservationWid(n);
				return wid == null || !takeOrderWids.has(wid);
			});
			return filterPendingByCopilotSelectedRole(merged);
		},

		getAllTableTakeOrderReminderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.TABLE_TAKE_ORDER_REMINDER),
			),

		/** Misma lógica de filtro por zona/rango que recordatorio ir a comandar. */
		getAllTableCleanReminderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.TABLE_CLEAN_REMINDER),
			),

		getNotificationsForType: (state, typeArray) => {
			return filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(state, (notification) => notification.type === typeArray),
			);
		},

		// Legacy getter for backward compatibility
		getTicketNotifications: (state) => {
			const list = state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || [];
			return filterPendingByCopilotSelectedRole(list);
		},

		getAllNotificationUpsellingDrinksReminder: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(n) => n.type === 'upselling_drinks_reminder' && n.status === 0,
				),
			),

		getNotificationById: (state) => (notificationId) => {
			if (notificationId == null) return null;
			const id = String(notificationId);
			const match = (list) => (list || []).find((n) => String(n?.id) === id);
			return match(state.notifications?.pending) || match(state.notifications?.history) || null;
		},

		getIncentiveGoalProductIds: (state) => {
			const ids = (state.incentiveGoalsProgress || []).flatMap(
				(g) => (g.products ?? []).map((p) => p?.id).filter(Boolean),
			);
			return [...new Set(ids.filter(Boolean))];
		},

		getAllReservationsNotificationUpsellingDrinksReminder: (state) => {
			const filteredNotifications = filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(notification) => notification.type === 'upselling_drinks_reminder' && notification.status === 0,
				),
			);

			const copilotReservations = store.getters['copilot/reservations'] || [];

			return filteredNotifications
				.map((notification) => {
					const reservationWid = notification.reservations?.[0]?.wid;
					const reservation = copilotReservations.find((r) => r.id === reservationWid);

					return {
						...reservation,
						notificationId: notification.id,
					};
				})
				.filter((r) => r != null);
		},

		// Get drinks for a specific reservation (migrated from Vuex)
		getAllDrinksForReservation: () => (reservation) => {
			if (reservation && reservation.cartSession) {
				const productOrders = reservation.cartSession?.productOrders || [];
				return productOrders
					.flatMap((order) => order.items || [])
					.filter((item) => {
						if (item.type) {
							return item.type.toLowerCase() === 'bebida';
						}
					});
			}

			return [];
		},

		// Loading states
		getIsLoadingNotifications: (state) => state.isLoadingNotifications,
		getIsLoadingTicketNotifications: (state) => state.isLoadingTicketNotifications,
		getLastUpdateTime: (state) => state.lastUpdateTime,

		// Zone filtering
		getSelectedZoneFilter: () => {
			return store.getters['copilot/selectedZoneFilter'] || [];
		},
	},

	actions: {
		// Main action for the unified notifications endpoint
		async loadNotifications(date = null) {
			try {
				this.isLoadingNotifications = true;
				this.notifications = await notificationRepository.getNotifications(date);
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading notifications:', error);
			} finally {
				this.isLoadingNotifications = false;
			}
		},

		/**
		 * Inserta al instante notificaciones Copilot con tarjeta visual (WebSocket).
		 * `account_seated` is audio-only in Sala (not in REST pending); do not upsert it here —
		 * audio still runs via `setNewNotification` in BackendSyncService.
		 */
		upsertPendingAccountSeatedFromPush(notification) {
			const visualTypes = ['table_take_order_reminder', 'table_clean_reminder'];
			if (!notification?.id || !visualTypes.includes(notification.type)) {
				return;
			}
			if (!this.notifications.pending) {
				this.notifications.pending = [];
			}
			const widIncoming = notification.reservations?.[0]?.wid;
			if (notification.type === 'table_take_order_reminder' && widIncoming != null) {
				const widStr = String(widIncoming);
				this.notifications.pending = this.notifications.pending.filter((n) => {
					if (n.type !== this.notificationsType.ACCOUNT_SEATED) return true;
					const w = n.reservations?.[0]?.wid;
					return w == null || String(w) !== widStr;
				});
			}
			if (this.notifications.pending.some((n) => n.id === notification.id)) {
				return;
			}
			const row = {
				...notification,
				status: notification.status !== undefined ? notification.status : 0,
			};
			this.notifications.pending = [row, ...this.notifications.pending];
		},

		// Legacy actions for backward compatibility (kept for existing code)
		async loadRecentNotifications() {
			try {
				this.isLoadingNotifications = true;
				const notifications = await notificationRepository.getRecentNotifications();
				this.notifications = { history: notifications, pending: [] };
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading recent notifications:', error);
			} finally {
				this.isLoadingNotifications = false;
			}
		},

		async loadTicketRequestNotifications() {
			try {
				this.isLoadingTicketNotifications = true;
				const ticketNotifications = await notificationRepository.getTicketRequestNotifications();
				this.notifications = { history: [], pending: ticketNotifications };
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading ticket request notifications:', error);
			} finally {
				this.isLoadingTicketNotifications = false;
			}
		},

		async loadAllNotifications() {
			// Use the new unified endpoint instead of separate calls
			await this.loadNotifications();
		},

		// Add a new notification (for real-time updates)
		addNotification(notification) {
			// Add to history notifications
			if (!this.notifications.history) {
				this.notifications.history = [];
			}
			this.notifications.history.unshift(notification);
		},

		// Update notification status (e.g., when acknowledged)
		updateNotification({ id, updates }) {
			// Update in history notifications
			if (this.notifications.history) {
				const index = this.notifications.history.findIndex((n) => n.id === id);
				if (index !== -1) {
					this.notifications.history[index] = { ...this.notifications.history[index], ...updates };
				}
			}
			// Update in pending notifications
			if (this.notifications.pending) {
				const index = this.notifications.pending.findIndex((n) => n.id === id);
				if (index !== -1) {
					this.notifications.pending[index] = { ...this.notifications.pending[index], ...updates };
				}
			}
		},

		// Remove notification (e.g., when dismissed)
		removeNotification(id) {
			// Remove from history notifications
			if (this.notifications.history) {
				this.notifications.history = this.notifications.history.filter((n) => n.id !== id);
			}
			// Remove from pending notifications
			if (this.notifications.pending) {
				this.notifications.pending = this.notifications.pending.filter((n) => n.id !== id);
			}
		},

		// Clear all notifications
		clearNotifications() {
			this.notifications = { history: [], pending: [] };
		},

		// Legacy action for backward compatibility
		async acknowledgeNotifications(notificationId, notificationData) {
			try {
				const response = await notificationRepository.acknowledgeNotification(notificationId, notificationData);

				if (response.ok) {
					// Remove the acknowledged notification
					this.removeNotification(notificationId);
					return response;
				} else {
					return response;
				}
			} catch (error) {
				console.error('Error acknowledging notification:', error);
				throw error;
			}
		},

		async loadUpsellingReportIfEnabled({ force = false } = {}) {
			if (!isUpsellingReportEnabled()) {
				return;
			}
			await this.loadNotificationsUpsellingData({ force });
		},

		async loadNotificationsUpsellingData({ force = false } = {}) {
			if (!force && this.lastUpsellingFetchAt != null) {
				const elapsed = Date.now() - this.lastUpsellingFetchAt;
				if (elapsed < UPSELLING_REPORT_TTL_MS) {
					return;
				}
			}
			if (this.isLoadingUpsellingReport) {
				return;
			}

			try {
				this.isLoadingUpsellingReport = true;
				const logicalDate = store.getters['copilot/selectedDate'];
				const response = await notificationRepository.getUpsellingNotificationsData(logicalDate, logicalDate);
				const next = response !== undefined && response !== null ? response : [];
				this.$patch({
					upsellingNotificationsData: next,
					lastUpsellingFetchAt: Date.now(),
				});
			} catch (error) {
				console.error('Error loading upselling notifications report', error);
				this.$patch({ upsellingNotificationsData: [] });
			} finally {
				this.isLoadingUpsellingReport = false;
			}
		},

		async loadIncentiveGoalsProgress({ force = false } = {}) {
			const staffId = store.getters['copilot/currentStaff']?.id;
			if (!staffId) {
				this.$patch({
					incentiveGoalsProgress: [],
					incentiveGoalsStaffId: null,
					lastIncentiveGoalsFetchAt: null,
				});
				return;
			}

			const staffChanged = this.incentiveGoalsStaffId !== staffId;
			if (!force && !staffChanged && this.lastIncentiveGoalsFetchAt != null) {
				const elapsed = Date.now() - this.lastIncentiveGoalsFetchAt;
				if (elapsed < INCENTIVE_GOALS_TTL_MS) {
					return;
				}
			}
			if (this.isLoadingIncentiveGoals) {
				return;
			}

			try {
				this.isLoadingIncentiveGoals = true;
				const data = await copilotRepository.getStaffIncentiveGoalsProgress(staffId);
				const goals = Array.isArray(data?.goals) ? data.goals : [];
				this.$patch({
					incentiveGoalsProgress: goals,
					incentiveGoalsStaffId: staffId,
					lastIncentiveGoalsFetchAt: Date.now(),
				});
			} catch (error) {
				console.error('Error loading incentive goals progress', error);
				this.$patch({
					incentiveGoalsProgress: [],
					incentiveGoalsStaffId: staffId,
				});
			} finally {
				this.isLoadingIncentiveGoals = false;
			}
		},
	},
});

/**
 * @param {object} notification
 * @returns {string|null}
 */
function notificationReservationWid(notification) {
	const w = notification?.reservations?.[0]?.wid;
	return w == null ? null : String(w);
}

/**
 * @returns {Set<string>}
 */
function getSelectedRangeTableNamesSet() {
	const raw = store.getters['copilot/selectedRangeTableNames'] || [];
	return raw instanceof Set ? raw : new Set(raw);
}

/**
 * Pending filtradas por predicado, luego por zona + selectedRangeTableNames (mismo helper que audio).
 * @param {{ notifications?: { pending?: unknown[] } }} state
 * @param {(n: object) => boolean} predicate
 */
function filterPendingBySelectedZonesAndRangeTableNames(state, predicate) {
	const notifications = (state.notifications?.pending || []).filter(predicate);
	const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];
	const allowedTables = getSelectedRangeTableNamesSet();

	if (selectedZones.length === 0 && allowedTables.size === 0) {
		return notifications;
	}

	return notifications.filter((notification) =>
		notificationMatchesZoneOrRangeFilter(notification, selectedZones, allowedTables),
	);
}

/**
 * Pending con status 0 y tipo dado, filtradas por zona y rangos (Vuex selectedRangeTableNames).
 * @param {{ notifications?: { pending?: unknown[] } }} state
 * @param {string} notificationType
 */
function filterPendingByTypeAndCopilotZoneRange(state, notificationType) {
	return filterPendingBySelectedZonesAndRangeTableNames(
		state,
		(notification) => notification.status === 0 && notification.type === notificationType,
	);
}
